import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTraces } from "@/lib/hooks/useTraces";
import { groupSpansByTrace } from "@/utils/trace-utils";
import { useMemo, useState } from "react";
import type { DockmasterSpan } from "../../../../../schemas/codegen";
import type {
  EnhancedSpan,
  EnhancedTrace,
} from "../../../CodeGen/Chat/components/traces/types";
import { McpRequestTraces } from "../../McpRequestTraces";

type FilterType = "all" | "tools" | "prompts" | "resources";

interface InspectorHistorySidebarProps {
  projectId: string;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  className?: string;
}

export function InspectorHistorySidebar({
  projectId,
  isExpanded,
}: InspectorHistorySidebarProps) {
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");

  // Fetch trace data
  const {
    data: tracesData,
    isLoading: tracesLoading,
    error: tracesError,
  } = useTraces(projectId);

  // Process traces
  const { enhancedTraces, filterCounts } = useMemo(() => {
    const rawTraces = tracesData?.traces ?? [];

    // Process traces using utility functions
    const enhancedTraces: EnhancedTrace[] = rawTraces.map((trace) => {
      if (!trace.spans || trace.spans.length === 0) {
        return {
          traceId: trace.traceId,
          startTime: 0,
          endTime: 0,
          duration: 0,
          spanCount: 0,
          hasErrors: false,
          spans: [],
        };
      }

      // Use utility to process the trace (cast spans to correct type)
      const [processedTrace] = groupSpansByTrace(
        trace.spans as DockmasterSpan[],
      );
      return (
        processedTrace || {
          traceId: trace.traceId,
          startTime: 0,
          endTime: 0,
          duration: 0,
          spanCount: 0,
          hasErrors: false,
          spans: [],
        }
      );
    });

    // Count traces by filter type
    let toolsCount = 0;
    let promptsCount = 0;
    let resourcesCount = 0;

    for (const trace of enhancedTraces) {
      let hasTools = false;
      let hasPrompts = false;
      let hasResources = false;

      for (const span of trace.spans as EnhancedSpan[]) {
        if (span.mcpToolName) {
          hasTools = true;
        }
        if (span.mcpMethodName?.startsWith("prompts/")) {
          hasPrompts = true;
        }
        if (span.mcpMethodName?.startsWith("resources/")) {
          hasResources = true;
        }
      }

      if (hasTools) {
        toolsCount++;
      }
      if (hasPrompts) {
        promptsCount++;
      }
      if (hasResources) {
        resourcesCount++;
      }
    }

    const filterCounts = {
      all: enhancedTraces.length,
      tools: toolsCount,
      prompts: promptsCount,
      resources: resourcesCount,
    };

    return { enhancedTraces, filterCounts };
  }, [tracesData]);

  // Filter traces based on active filter
  const filteredTraces = useMemo(() => {
    if (activeFilter === "all") {
      return enhancedTraces;
    }

    return enhancedTraces.filter((trace) => {
      for (const span of trace.spans as EnhancedSpan[]) {
        switch (activeFilter) {
          case "tools":
            if (span.mcpToolName) {
              return true;
            }
            break;
          case "prompts":
            if (span.mcpMethodName?.startsWith("prompts/")) {
              return true;
            }
            break;
          case "resources":
            if (span.mcpMethodName?.startsWith("resources/")) {
              return true;
            }
            break;
        }
      }
      return false;
    });
  }, [enhancedTraces, activeFilter]);

  const filterOptions: Array<{ key: FilterType; label: string }> = [
    { key: "all", label: "All" },
    { key: "tools", label: "Tools" },
    { key: "prompts", label: "Prompts" },
    { key: "resources", label: "Resources" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b pb-1">
        <div className="text-lg flex items-center gap-2">History</div>
      </div>

      {isExpanded && (
        <div className="space-y-4">
          {/* Filter Controls */}
          <div className="flex flex-wrap gap-2">
            {filterOptions.map((filter) => (
              <Button
                key={filter.key}
                variant={activeFilter === filter.key ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveFilter(filter.key)}
                className="flex items-center gap-2"
              >
                {filter.label}
                <Badge variant="secondary" className="ml-1 h-4 px-1.5">
                  {filterCounts[filter.key]}
                </Badge>
              </Button>
            ))}
          </div>

          {/* Traces Display */}
          <div className="space-y-4">
            <McpRequestTraces
              projectId={projectId}
              traces={filteredTraces}
              isLoading={tracesLoading}
              error={tracesError}
              className="border-0 shadow-none bg-transparent px-0"
            />
          </div>
        </div>
      )}
    </div>
  );
}
